import React, { useMemo } from 'react';
// Deep module import, NOT the package root, and deliberately so.
//
// `ownerRef` itself is pure zod and perfectly client-safe, but the root barrel
// re-exports `brokerIdentity`, `brokerOfficeRouting`, `clientIdentity` and
// `loanFingerprinting`, all of which `require('crypto')`. Importing any runtime
// VALUE from '@valutten/shared-schemas' therefore pulls Node crypto into the
// browser bundle and fails the build outright:
//
//   Module not found: Error: Can't resolve 'crypto' in
//   'packages/shared-schemas/dist'
//
// Type-only imports are erased by the compiler, which is why every other client
// import from this package gets away with the root path. Reaching the compiled
// module directly is the established pattern here: CrmStageMappingsPage and
// LeadPipelinePage already import '@valutten/shared-schemas/dist/crmStageMapping'
// for the same reason. Collapse these to a clean subpath once shared-schemas
// declares an `exports` map.
import { makeOwnerRef, type OwnerRefType } from '@valutten/shared-schemas/dist/ownerRef';
import { useCreditAnalystRecords } from '../team/v2/hooks/useCreditAnalystRecords';
import { useBrokerDocs } from '../team/v2/hooks/useBrokerDocs';

/**
 * `makeOwnerRef` throws on an id that would not survive the ref regex, which is
 * right for a write path: a payload must never carry an unparseable owner. Here
 * it runs while rendering a list, so one unusual document id should drop that
 * one option rather than take the page down. Nulls are filtered out below, so
 * an unmintable ref still cannot reach a payload.
 */
function safeOwnerRef(type: OwnerRefType, id: string): string | null {
  try {
    return makeOwnerRef(type, id);
  } catch {
    return null;
  }
}

/**
 * Owner picker for Team Activity.
 *
 * WHY THREE GROUPS AND NOT ONE LIST
 *
 * "Whose work is this" has three different record types behind it, and they are
 * genuinely different things rather than three views of one thing:
 *
 *   Team members  a person who authenticates. Keyed by uid.
 *   Analysts      an operational credit_analyst record, which may exist with
 *                 NO login at all. Keyed by its document id, which is why the
 *                 ref is not a uid.
 *   Brokers       a broker document, the identity commission data attaches to.
 *
 * There is deliberately NO cross-group dedup by name. Name matching is banned
 * repo-wide because it is exactly how two different people get merged, and one
 * person legitimately appearing as both a team member and a broker is normal:
 * the group label is what disambiguates them, and each ref points at a
 * different record.
 *
 * DATA SOURCES ARE ALL PRE-EXISTING. Analysts come from the
 * `listCreditAnalystRecords` callable via the Team page's hook (same callable,
 * same role gate: super_admin / administrator / office_admin). Brokers come
 * from the Team page's broker-docs read, with the same
 * `!mergedInto && status !== 'inactive'` filter used in AssignmentsTab and
 * TeamManagementPage. Team members come from the AppLayout outlet context,
 * already loaded for every page. Nothing here opens a new read path.
 */

export interface OwnerOption {
  /** Namespaced ref: the value that goes on the wire. */
  ref: string;
  label: string;
  /** Rendered inline after the label, e.g. an analyst with no login. */
  badge?: string;
}

export interface OwnerOptionGroup {
  key: string;
  label: string;
  options: OwnerOption[];
}

/** Shape of a company user as the outlet context already supplies it. */
export interface TeamMemberLike {
  id: string;
  displayName?: string;
  email?: string;
}

export function useOwnerOptions(
  companyId: string | undefined,
  enabled: boolean,
  teamMembers: TeamMemberLike[] | undefined
): OwnerOptionGroup[] {
  // Both hooks are called unconditionally (rules of hooks); passing an
  // undefined companyId is how they stay dormant for a role that gets no
  // picker, so no extra read is issued for a broker viewing their own rows.
  const { records: analystRecords } = useCreditAnalystRecords(companyId, enabled);
  const { brokerDocs } = useBrokerDocs(enabled ? companyId : undefined);

  return useMemo(() => {
    if (!enabled) return [];

    const users: OwnerOption[] = (teamMembers ?? []).flatMap((m) => {
      const ref = safeOwnerRef('user', m.id);
      if (!ref) return [];
      return [{ ref, label: m.displayName || m.email || m.id }];
    });

    const analysts: OwnerOption[] = analystRecords
      .filter((r) => r.status === 'active')
      .flatMap((r) => {
        const ref = safeOwnerRef('analyst', r.analystId);
        if (!ref) return [];
        return [{
          ref,
          label: r.displayName || r.analystId,
          ...(r.hasLogin ? {} : { badge: 'no login' }),
        }];
      });

    const brokers: OwnerOption[] = brokerDocs
      .filter((b) => !b.mergedInto && b.status !== 'inactive')
      .flatMap((b) => {
        const ref = safeOwnerRef('broker', b.id);
        if (!ref) return [];
        return [{ ref, label: b.currentName }];
      });

    return [
      { key: 'user', label: 'Team members', options: users },
      { key: 'analyst', label: 'Analysts', options: analysts },
      { key: 'broker', label: 'Brokers', options: brokers },
    ].filter((g) => g.options.length > 0);
  }, [enabled, teamMembers, analystRecords, brokerDocs]);
}

/**
 * Owner ref to display name, built from the SAME options the picker renders.
 *
 * One source for the label is the point. A second lookup built from a different
 * list is how the "Who" column ends up naming someone the picker cannot select,
 * or worse, naming the wrong record: the three id spaces (user, analyst,
 * broker) overlap freely, so only the full namespaced ref identifies a person.
 *
 * A ref with no option is deliberately NOT in the map. Callers fall back to the
 * raw ref, which is honest: an unresolvable owner is a real condition (a
 * departed team member, an analyst record since deactivated) and inventing a
 * label like "Unknown" would hide which row needs attention.
 */
export function useOwnerLabelMap(groups: OwnerOptionGroup[]): Map<string, string> {
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) {
      for (const o of g.options) map.set(o.ref, o.label);
    }
    return map;
  }, [groups]);
}

interface OwnerSelectProps {
  value: string;
  onChange: (ref: string) => void;
  groups: OwnerOptionGroup[];
  /** Label for the empty value. Differs by use: a filter versus a form. */
  emptyLabel: string;
  className: string;
  id?: string;
}

/**
 * One grouped `<select>`, following the app's existing `<optgroup>` pattern
 * (PerformanceTargetsTab, BrokerCommissionSettingsPage). The empty value is a
 * real option rather than a placeholder attribute, because both callers need it
 * to be selectable: it means "everyone" on the filter and "me" on the form.
 */
export function OwnerSelect({
  value, onChange, groups, emptyLabel, className, id,
}: OwnerSelectProps): React.ReactElement {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={className}>
      <option value="">{emptyLabel}</option>
      {groups.map((g) => (
        <optgroup key={g.key} label={g.label}>
          {g.options.map((o) => (
            <option key={o.ref} value={o.ref}>
              {o.badge ? `${o.label} (${o.badge})` : o.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
